/**
 * NotificationSettings — a person's own rules over the notification
 * catalogue (notifications part two).
 *
 * /t/:tenantSlug/settings/notifications. One row per catalogue event,
 * in catalogue order (the server's own order — NOTIFICATION_EVENTS_IN_ORDER):
 * a channel control, a cadence control (disabled when the channel isn't
 * email), a "Default" marker when the person never chose, and "Reset
 * to default" when they did. No settings navigation exists yet, so
 * this page is reached from the bell panel footer and the user menu
 * (src/lib/navSection.ts) rather than nested under one.
 *
 * When email is suppressed (a bounce), a caution banner leads the page
 * and every channel control goes read-only — changing cadence or
 * resetting still works (those don't touch whether email itself is
 * suppressed), but a person cannot ask for email that will not send.
 */

import { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import {
  useNotificationPreferences,
  useSetNotificationPreference,
  useResetNotificationPreference,
} from '@/lib/notifications';
import type { EffectiveNotificationPreferenceRow, NotificationRule } from '@/lib/api';
import { PageShell } from '@/components/primitives/PageShell';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { SelectField } from '@/components/primitives/SelectField';

const CHANNEL_OPTIONS: ReadonlyArray<{ value: NotificationRule['channel']; label: string }> = [
  { value: 'email_and_in_app', label: 'Email and in-app' },
  { value: 'in_app', label: 'In-app only' },
  { value: 'off', label: 'Off' },
];

const CADENCE_OPTIONS: ReadonlyArray<{ value: NotificationRule['cadence']; label: string }> = [
  { value: 'immediate', label: 'Immediately' },
  { value: 'daily', label: 'Daily digest at 7am' },
];

interface RowProps {
  row: EffectiveNotificationPreferenceRow;
  readOnly: boolean;
  onChange: (rule: NotificationRule) => void;
  onReset: () => void;
}

function PreferenceRow({ row, readOnly, onChange, onReset }: RowProps): ReactNode {
  const rule = row.effective;
  const cadenceDisabled = readOnly || rule.channel !== 'email_and_in_app';

  return (
    <li className="py-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-sage-800/[8%] last:border-b-0">
      <div className="min-w-0 sm:max-w-sm">
        <p className="text-sm font-medium text-ink">{row.label}</p>
        <p className="mt-0.5 text-xs text-ink-soft">{row.description}</p>
        {row.own_rule === null ? (
          <span
            data-testid={`default-marker-${row.event_key}`}
            className="mt-1 inline-block text-xs font-medium text-sage-600"
          >
            Default
          </span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`reset-${row.event_key}`}
            onClick={onReset}
            className="mt-1 -ml-3.5 text-warm-600 hover:text-warm-700"
          >
            Reset to default
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3 min-w-0 sm:flex-row sm:flex-wrap sm:items-center">
        <SelectField size="default" className="w-full sm:w-auto sm:shrink-0"
          aria-label={`Channel for ${row.label}`}
          data-testid={`channel-${row.event_key}`}
          value={rule.channel}
          disabled={readOnly}
          onChange={(e) =>
            onChange({ channel: e.target.value as NotificationRule['channel'], cadence: rule.cadence })
          }
        >
          {CHANNEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectField>

        <SelectField size="default" className="w-full sm:w-auto sm:shrink-0"
          aria-label={`Cadence for ${row.label}`}
          data-testid={`cadence-${row.event_key}`}
          value={rule.cadence}
          disabled={cadenceDisabled}
          onChange={(e) =>
            onChange({ channel: rule.channel, cadence: e.target.value as NotificationRule['cadence'] })
          }
        >
          {CADENCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SelectField>
      </div>
    </li>
  );
}

export function NotificationSettings(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const tenantSlug = routeTenantSlug ?? '';
  const { whoami } = useTenant();

  const { view, isLoading, isError, error } = useNotificationPreferences(tenantSlug);
  const setRule = useSetNotificationPreference(tenantSlug);
  const resetRule = useResetNotificationPreference(tenantSlug);

  const suppressed = view?.email_suppressed ?? false;

  return (
    <PageShell title="Notification settings" maxWidth="lg">
      {suppressed && (
        <div
          role="alert"
          data-testid="email-suppressed-banner"
          className="mb-4 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
        >
          Email to you is switched off because a message bounced. Ask the office to fix your address.
        </div>
      )}

      <AsyncBoundary
        isLoading={isLoading || whoami === null}
        isError={isError}
        error={error}
        emptyHeadline="Once there are events to set, you'll see them here."
      >
        {view && (
          <ul>
            {view.events.map((row) => (
              <PreferenceRow
                key={row.event_key}
                row={row}
                readOnly={suppressed}
                onChange={(rule) => setRule.mutate({ eventKey: row.event_key, rule })}
                onReset={() => resetRule.mutate(row.event_key)}
              />
            ))}
          </ul>
        )}
      </AsyncBoundary>
    </PageShell>
  );
}
